import React from "react";
import { useStore } from "../../store";

export const SettingsContent: React.FC = () => {
  const {
    alertServiceEnabled,
    toggleAlertService,
    craneParameters,
    updateCraneParameter,
  } = useStore();

  return (
    <div className="space-y-6">
      <div className="pt-6">
        <h3 className="text-lg font-semibold mb-4">Development Tools</h3>

        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <span className="text-sm font-medium">Random Alert Generator</span>
            <p className="text-xs text-crane-gray">
              Generate random alerts every ~30 seconds for testing
            </p>
          </div>

          <button
            onClick={toggleAlertService}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              alertServiceEnabled
                ? "bg-crane-selection-green"
                : "bg-crane-darker"
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                alertServiceEnabled ? "translate-x-6" : "translate-x-1"
              }`}
            />
          </button>
        </div>

        {alertServiceEnabled && (
          <div className="mt-2 text-xs text-crane-selection-green">
            ✓ Alert service is running
          </div>
        )}
      </div>

      <div className="pt-6">
        <h3 className="text-lg font-semibold mb-4">Indicator Settings</h3>

        {/* Speed Limits */}
        <div className="space-y-4 mb-6">
          <div>
            <h4 className="text-sm font-medium mb-3">Speed Limits</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-crane-gray mb-1">
                  Maximum Speed (%)
                </label>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() =>
                      updateCraneParameter(
                        "speedMax",
                        Math.max(
                          craneParameters.speedMin,
                          craneParameters.speedMax - 1
                        )
                      )
                    }
                    className="w-6 h-6 rounded bg-crane-darker flex items-center justify-center text-white text-sm hover:bg-crane-gray transition-colors"
                  >
                    -
                  </button>
                  <input
                    type="number"
                    value={craneParameters.speedMax}
                    onChange={(e) =>
                      updateCraneParameter(
                        "speedMax",
                        Math.max(
                          craneParameters.speedMin,
                          Math.max(
                            -100,
                            Math.min(100, parseInt(e.target.value) || 0)
                          )
                        )
                      )
                    }
                    className="w-20 px-2 py-1 bg-crane-darker border border-crane-gray rounded text-white text-sm text-center"
                    min={craneParameters.speedMin}
                    max={100}
                  />
                  <button
                    onClick={() =>
                      updateCraneParameter(
                        "speedMax",
                        Math.min(100, craneParameters.speedMax + 1)
                      )
                    }
                    className="w-6 h-6 rounded bg-crane-darker flex items-center justify-center text-white text-sm hover:bg-crane-gray transition-colors"
                  >
                    +
                  </button>
                  <span className="text-xs text-crane-gray">%</span>
                </div>
              </div>

              <div>
                <label className="block text-xs text-crane-gray mb-1">
                  Minimum Speed (%)
                </label>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() =>
                      updateCraneParameter(
                        "speedMin",
                        Math.min(
                          craneParameters.speedMax,
                          craneParameters.speedMin - 1
                        )
                      )
                    }
                    className="w-6 h-6 rounded bg-crane-darker flex items-center justify-center text-white text-sm hover:bg-crane-gray transition-colors"
                  >
                    -
                  </button>
                  <input
                    type="number"
                    value={craneParameters.speedMin}
                    onChange={(e) =>
                      updateCraneParameter(
                        "speedMin",
                        Math.min(
                          craneParameters.speedMax,
                          Math.max(
                            -100,
                            Math.min(100, parseInt(e.target.value) || 0)
                          )
                        )
                      )
                    }
                    className="w-20 px-2 py-1 bg-crane-darker border border-crane-gray rounded text-white text-sm text-center"
                    min={-100}
                    max={craneParameters.speedMax}
                  />
                  <button
                    onClick={() =>
                      updateCraneParameter(
                        "speedMin",
                        Math.min(
                          craneParameters.speedMax,
                          craneParameters.speedMin + 1
                        )
                      )
                    }
                    className="w-6 h-6 rounded bg-crane-darker flex items-center justify-center text-white text-sm hover:bg-crane-gray transition-colors"
                  >
                    +
                  </button>
                  <span className="text-xs text-crane-gray">%</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Boom Angle Limits */}
        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-medium mb-3">Boom Angle Limits</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-crane-gray mb-1">
                  Maximum Angle
                </label>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() =>
                      updateCraneParameter(
                        "boomMaxAngle",
                        Math.max(0, craneParameters.boomMaxAngle - 1)
                      )
                    }
                    className="w-6 h-6 rounded bg-crane-darker flex items-center justify-center text-white text-sm hover:bg-crane-gray transition-colors"
                  >
                    -
                  </button>
                  <input
                    type="number"
                    value={craneParameters.boomMaxAngle}
                    onChange={(e) =>
                      updateCraneParameter(
                        "boomMaxAngle",
                        Math.max(0, parseInt(e.target.value) || 0)
                      )
                    }
                    className="w-16 px-2 py-1 bg-crane-darker border border-crane-gray rounded text-white text-sm text-center"
                    min="0"
                    max="90"
                  />
                  <button
                    onClick={() =>
                      updateCraneParameter(
                        "boomMaxAngle",
                        Math.min(90, craneParameters.boomMaxAngle + 1)
                      )
                    }
                    className="w-6 h-6 rounded bg-crane-darker flex items-center justify-center text-white text-sm hover:bg-crane-gray transition-colors"
                  >
                    +
                  </button>
                  <span className="text-xs text-crane-gray">°</span>
                </div>
              </div>

              <div>
                <label className="block text-xs text-crane-gray mb-1">
                  Minimum Angle
                </label>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() =>
                      updateCraneParameter(
                        "boomMinAngle",
                        Math.max(0, craneParameters.boomMinAngle - 1)
                      )
                    }
                    className="w-6 h-6 rounded bg-crane-darker flex items-center justify-center text-white text-sm hover:bg-crane-gray transition-colors"
                  >
                    -
                  </button>
                  <input
                    type="number"
                    value={craneParameters.boomMinAngle}
                    onChange={(e) =>
                      updateCraneParameter(
                        "boomMinAngle",
                        Math.max(0, parseInt(e.target.value) || 0)
                      )
                    }
                    className="w-16 px-2 py-1 bg-crane-darker border border-crane-gray rounded text-white text-sm text-center"
                    min="0"
                    max="90"
                  />
                  <button
                    onClick={() =>
                      updateCraneParameter(
                        "boomMinAngle",
                        Math.min(90, craneParameters.boomMinAngle + 1)
                      )
                    }
                    className="w-6 h-6 rounded bg-crane-darker flex items-center justify-center text-white text-sm hover:bg-crane-gray transition-colors"
                  >
                    +
                  </button>
                  <span className="text-xs text-crane-gray">°</span>
                </div>
              </div>
            </div>
          </div>

          {/* Slew Angle Limits */}
          <div>
            <h4 className="text-sm font-medium mb-3">Slew Angle Limits</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-crane-gray mb-1">
                  Maximum Angle
                </label>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() =>
                      updateCraneParameter(
                        "slewMaxAngle",
                        Math.max(-180, craneParameters.slewMaxAngle - 1)
                      )
                    }
                    className="w-6 h-6 rounded bg-crane-darker flex items-center justify-center text-white text-sm hover:bg-crane-gray transition-colors"
                  >
                    -
                  </button>
                  <input
                    type="number"
                    value={craneParameters.slewMaxAngle}
                    onChange={(e) =>
                      updateCraneParameter(
                        "slewMaxAngle",
                        Math.max(
                          -180,
                          Math.min(180, parseInt(e.target.value) || 0)
                        )
                      )
                    }
                    className="w-16 px-2 py-1 bg-crane-darker border border-crane-gray rounded text-white text-sm text-center"
                    min="-180"
                    max="180"
                  />
                  <button
                    onClick={() =>
                      updateCraneParameter(
                        "slewMaxAngle",
                        Math.min(180, craneParameters.slewMaxAngle + 1)
                      )
                    }
                    className="w-6 h-6 rounded bg-crane-darker flex items-center justify-center text-white text-sm hover:bg-crane-gray transition-colors"
                  >
                    +
                  </button>
                  <span className="text-xs text-crane-gray">°</span>
                </div>
              </div>

              <div>
                <label className="block text-xs text-crane-gray mb-1">
                  Minimum Angle
                </label>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() =>
                      updateCraneParameter(
                        "slewMinAngle",
                        Math.max(-180, craneParameters.slewMinAngle - 1)
                      )
                    }
                    className="w-6 h-6 rounded bg-crane-darker flex items-center justify-center text-white text-sm hover:bg-crane-gray transition-colors"
                  >
                    -
                  </button>
                  <input
                    type="number"
                    value={craneParameters.slewMinAngle}
                    onChange={(e) =>
                      updateCraneParameter(
                        "slewMinAngle",
                        Math.max(
                          -180,
                          Math.min(180, parseInt(e.target.value) || 0)
                        )
                      )
                    }
                    className="w-16 px-2 py-1 bg-crane-darker border border-crane-gray rounded text-white text-sm text-center"
                    min="-180"
                    max="180"
                  />
                  <button
                    onClick={() =>
                      updateCraneParameter(
                        "slewMinAngle",
                        Math.min(180, craneParameters.slewMinAngle + 1)
                      )
                    }
                    className="w-6 h-6 rounded bg-crane-darker flex items-center justify-center text-white text-sm hover:bg-crane-gray transition-colors"
                  >
                    +
                  </button>
                  <span className="text-xs text-crane-gray">°</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
